import { ArrowLeftIcon, ArrowRightIcon } from "icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SlickSlider, { Settings } from "react-slick";

interface ISliderProps {
  settings: Settings;
  children: React.ReactNode;
  playOnHover?: boolean;
  className?: string;
  initialSlideOnMobile?: number;
}

const SlickButtonFix = (props: {
  children: JSX.Element;
  slideCount?: number;
  currentSlide?: number;
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, currentSlide, slideCount, ...others } = props;
  return <div {...others}>{children}</div>;
};

export function MobileAwareSlider({
  settings,
  children,
  playOnHover = false,
  className = "mx-5 lg:-mx-2.5",
  initialSlideOnMobile,
}: ISliderProps): JSX.Element {
  const [autoplay, setAutoplay] = useState(false);
  const [isSettingsUpdated, setIsSettingsUpdated] = useState(false);
  const sliderRef = useRef<SlickSlider | null>(null);

  // Memoized slider settings
  const updatedSettings = useMemo(
    () =>
      playOnHover
        ? {
            ...settings,
            autoplay,
            autoplaySpeed: 2000,
          }
        : settings,
    [playOnHover, autoplay, settings]
  );

  const [sliderSettings, setSliderSettings] = useState(updatedSettings); // State to track screen size

  useEffect(() => {
    const checkMobile = (): void => {
      const newSettings = { ...updatedSettings };
      if (window.innerWidth <= 600) {
        newSettings.initialSlide =
          initialSlideOnMobile !== undefined
            ? initialSlideOnMobile
            : updatedSettings.initialSlide;
      } else if (window.innerWidth <= 1274 && window.innerWidth > 795) {
        newSettings.initialSlide =
          initialSlideOnMobile !== undefined
            ? initialSlideOnMobile - 1
            : updatedSettings.initialSlide;
        newSettings.centerMode = false;
        newSettings.arrows = true;
      } else if (window.innerWidth <= 795 && window.innerWidth > 600) {
        newSettings.initialSlide =
          initialSlideOnMobile !== undefined
            ? initialSlideOnMobile
            : updatedSettings.initialSlide;
        newSettings.centerMode = true;
        newSettings.arrows = true;
      } else {
        newSettings.centerMode = false;
        newSettings.arrows = false;
      }

      setSliderSettings(newSettings);
      setIsSettingsUpdated(true);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, [initialSlideOnMobile, updatedSettings]);

  const handleMouseEnter = (): void => {
    setAutoplay(true);
    sliderRef.current?.slickPlay();
  };

  const handleMouseLeave = (): void => {
    setAutoplay(false);
    sliderRef.current?.slickPause();
  };

  const handleFocus = (): void => {
    if (!autoplay) {
      handleMouseEnter();
    }
  };

  const handleBlur = (): void => {
    if (autoplay) {
      handleMouseLeave();
    }
  };

  return (
    <div
      {...(playOnHover && {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onMouseOver: handleMouseEnter,
        onMouseOut: handleMouseLeave,
        onFocus: handleFocus,
        onBlur: handleBlur,
      })}
      role="region"
    >
      {isSettingsUpdated && (
        <SlickSlider
          {...sliderSettings}
          ref={sliderRef}
          className={className}
          prevArrow={
            <SlickButtonFix>
              <ArrowLeftIcon className="h-5 w-5" />
            </SlickButtonFix>
          }
          nextArrow={
            <SlickButtonFix>
              <ArrowRightIcon className="h-5 w-5" />
            </SlickButtonFix>
          }
        >
          {children}
        </SlickSlider>
      )}
    </div>
  );
}
